import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import { Button, FloatButton, Modal } from 'antd'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { ObjectModal } from './components/object-modal'
import { useModal } from '../../../common/hooks/formik/modal.hook'
import { ModalBottom } from '../../../common/ui/modal/modal-bottom'
import { PureTable } from '../../../common/ui/pure-table/pure-table'
import { DuLabel } from '../../../entities/du-labels/types/du-label.types'
import { useCreateObject, useDeleteObject, useGetObjects, useUpdateObject } from '../../../entities/objects/hooks/object.hooks'
import { ObjectItem } from '../../../entities/objects/types/object.types'
import { PageHeader } from '../../shared/components/page-header'

export const ObjectsPage = () => {
  const { t } = useTranslation()
  const { data: apiObjects } = useGetObjects()
  const [objects, setObjects] = useState<ObjectItem[]>([])
  const [selectedObject, setSelectedObject] = useState<ObjectItem | null>(null)

  const objectModal = useModal(false)
  const objectModalFormRef = useRef<{ submit: () => void } | null>(null)
  const { mutateAsync: createObject } = useCreateObject()
  const { mutateAsync: updateObject } = useUpdateObject()
  const { mutateAsync: deleteObject } = useDeleteObject()

  useEffect(() => {
    setObjects(apiObjects ?? [])
  }, [apiObjects])

  const handleSubmitForm = async (values: Omit<ObjectItem, 'id' | 'type' | 'createdAt' | 'updatedAt'>) => {
    if (selectedObject) {
      const updatedObject = await updateObject({
        ...values,
        id: selectedObject.id,
      })
      setObjects(objects.map((obj) => (obj.id === selectedObject.id ? updatedObject.data : obj)))
    } else {
      const object = await createObject(values)
      setObjects([...objects, object.data])
    }
    objectModal.close()
    setSelectedObject(null)
  }

  const handleDeleteObject = async (id: number) => {
    try {
      const status = await deleteObject(id)
      if (status === 200) {
        setObjects(objects.filter((obj) => obj.id !== id))
        objectModal.close()
        toast.success(t('objectsPage.objectDeleteSuccess'))
      }
    } catch {
      toast.error(t('objectsPage.errors.objectDeleteError'))
    }
  }

  const columns = [
    {
      title: t('objectsPage.objectName'),
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: ObjectItem) => (
        <span
          onClick={() => {
            setSelectedObject(record)
            objectModal.open()
          }}
          style={{ cursor: 'pointer', color: '#1890ff' }}
        >
          {text}
        </span>
      ),
    },
    {
      title: t('objectsPage.address'),
      dataIndex: 'address',
      key: 'address',
    },
    {
      title: t('objectsPage.latitude'),
      dataIndex: 'latitude',
      key: 'latitude',
    },
    {
      title: t('objectsPage.longitude'),
      dataIndex: 'longitude',
      key: 'longitude',
    },
    {
      title: t('objectsPage.objectType'),
      dataIndex: 'structureType',
      key: 'structureType',
      render: (_: unknown, record: { categoryDetail?: DuLabel }) => (
        <span>{record?.categoryDetail?.name ?? t('objectsPage.categoryNotProvided')}</span>
      ),
    },
    {
      title: t('common.actions'),
      key: 'actions',
      render: (_: unknown, record: { id: number; name: string }) => (
        <Button
          color="danger"
          onClick={() => {
            Modal.confirm({
              title: t('objectsPage.deleteConfirmTitle'),
              content: `${record.name} ${t('objectsPage.deleteDescription')}`,
              onOk: () => handleDeleteObject(record.id),
            })
          }}
          variant="text"
        >
          <DeleteOutlined />
        </Button>
      ),
    },
  ]

  return (
    <div>
      <PageHeader
        description={t('objectsPage.description')}
        header={t('objectsPage.header')}
      />
      <PureTable
        columns={columns}
        dataSource={objects}
        rowKey="id"
        searchPlaceholder={t('objectsPage.searchPlaceholder')}
        searchEnabled
      />
      <FloatButton
        icon={<PlusOutlined />}
        onClick={() => {
          setSelectedObject(null)
          objectModal.open()
        }}
        type="primary"
      />
      <ModalBottom
        cancelText={t('common.cancel')}
        okText={t('common.save')}
        onCancel={objectModal.close}
        onOk={() => objectModalFormRef.current?.submit()}
        open={objectModal.opened}
        title={selectedObject?.name ?? t('objectsPage.addNewObject')}
      >
        <ObjectModal
          ref={objectModalFormRef}
          initialValues={selectedObject}
          onSubmit={handleSubmitForm}
        />
      </ModalBottom>
    </div>
  )
}
